import { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { MyDomain } from "../../App";

const COUNTRIES = [
  { label: "🇮🇱 Israel (+972)", value: "+972", code: "IL", flag: "🇮🇱" },
  { label: "🇱🇧 Lebanon (+961)", value: "+961", code: "LB", flag: "🇱🇧" },
  { label: "🇹🇷 Türkiye (+90)", value: "+90", code: "TR", flag: "🇹🇷" },
  { label: "🇰🇼 Kuwait (+965)", value: "+965", code: "KW", flag: "🇰🇼" },
  { label: "🇵🇸 Palestine (+970)", value: "+970", code: "PS", flag: "🇵🇸" },
  { label: "🇸🇦 Saudi Arabia (+966)", value: "+966", code: "SA", flag: "🇸🇦" },
  { label: "🇯🇴 Jordan (+962)", value: "+962", code: "JO", flag: "🇯🇴" },
  { label: "🇦🇪 UAE (+971)", value: "+971", code: "AE", flag: "🇦🇪" },
  { label: "🇶🇦 Qatar (+974)", value: "+974", code: "QA", flag: "🇶🇦" },
  { label: "🇩🇪 Germany (+49)", value: "+49", code: "DE", flag: "🇩🇪" },
  { label: "🇸🇪 Sweden (+46)", value: "+46", code: "SE", flag: "🇸🇪" },
  { label: "🇳🇱 Netherlands (+31)", value: "+31", code: "NL", flag: "🇳🇱" },
  { label: "🇳🇴 Norway (+47)", value: "+47", code: "NO", flag: "🇳🇴" },
];

const SMS_COOLDOWN_SECONDS = 30;

// ── OTP Input Component ──
function OTPInput({ code, setCode, length = 6 }) {
  const inputRefs = useRef([]);

  const handleChange = (e, index) => {
    const raw = e.target.value;
    const normalized = raw.replace(/[٠-٩]/g, (d) =>
      "٠١٢٣٤٥٦٧٨٩".indexOf(d).toString(),
    );
    const numericText = normalized.replace(/[^0-9]/g, "");

    if (numericText === "") {
      const arr = code.split("");
      arr[index] = "";
      setCode(arr.join(""));
      return;
    }

    if (numericText.length > 1) {
      const arr = code.split("");
      numericText
        .slice(0, length)
        .split("")
        .forEach((digit, i) => {
          if (index + i < length) arr[index + i] = digit;
        });
      setCode(arr.join(""));
      const next = Math.min(index + numericText.length, length - 1);
      inputRefs.current[next]?.focus();
      return;
    }

    const arr = code.split("");
    arr[index] = numericText;
    setCode(arr.join(""));
    if (index < length - 1) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      const current = code[index];
      if (!current && index > 0) {
        const arr = code.split("");
        arr[index - 1] = "";
        setCode(arr.join(""));
        inputRefs.current[index - 1]?.focus();
      } else if (current) {
        const arr = code.split("");
        arr[index] = "";
        setCode(arr.join(""));
      }
    }
  };

  return (
    <div style={s.otpContainer}>
      {Array(length)
        .fill(0)
        .map((_, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            className="otp-input"
            style={{
              ...s.otpInput,
              ...(code[index] ? s.otpInputFilled : {}),
            }}
            value={code[index] || ""}
            onChange={(e) => handleChange(e, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onFocus={(e) => e.target.select()}
            inputMode="numeric"
            maxLength={1}
            type="text"
            autoComplete="one-time-code"
            dir="ltr"
          />
        ))}
    </div>
  );
}

// ── Country Dropdown Component ──
function CountryDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);
  const selected = COUNTRIES.find((c) => c.value === value);

  const filtered = COUNTRIES.filter((c) =>
    c.label.toLowerCase().includes(search.toLowerCase()),
  );

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} style={s.dropdownWrapper}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={s.dropdownButton}
      >
        <span style={{ fontSize: 18 }}>{selected?.flag}</span>
        <span style={s.dropdownCode}>{selected?.value}</span>
        <span style={s.dropdownChevron}>{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div style={s.dropdownMenu}>
          <input
            style={s.dropdownSearch}
            placeholder="Search country..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          <div style={s.dropdownList}>
            {filtered.map((c) => (
              <div
                key={c.value}
                className="dropdown-item"
                style={{
                  ...s.dropdownItem,
                  ...(c.value === value ? s.dropdownItemActive : {}),
                }}
                onClick={() => {
                  onChange(c);
                  setOpen(false);
                  setSearch("");
                }}
              >
                {c.label}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Login Component ──
export default function BarberSubsLogin() {
  const [callingCode, setCallingCode] = useState("+972");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [name, setName] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");
  const [openSecondPageLogin, setOpenSecondPageLogin] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);

  const isSendingRef = useRef(false);
  const isVerifyingRef = useRef(false);
  const isLoggingInRef = useRef(false);
  const cooldownTimerRef = useRef(null);
  const lastSmsSentTimeRef = useRef(0);

  useEffect(() => {
    return () => {
      if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (cooldownSeconds > 0) {
      cooldownTimerRef.current = setTimeout(() => {
        setCooldownSeconds((p) => p - 1);
      }, 1000);
    } else {
      isSendingRef.current = false;
    }
    return () => {
      if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
    };
  }, [cooldownSeconds]);

  const canSendSms = useCallback(() => {
    const now = Date.now();
    const elapsed = now - lastSmsSentTimeRef.current;
    const cooldownMs = SMS_COOLDOWN_SECONDS * 1000;
    if (lastSmsSentTimeRef.current > 0 && elapsed < cooldownMs) {
      const rem = Math.ceil((cooldownMs - elapsed) / 1000);
      showMessage(`You can send a new code in ${rem} seconds.`, "warning");
      return false;
    }
    return true;
  }, []);

  const showMessage = (msg, type = "info") => {
    setMessage(msg);
    setMessageType(type);
  };

  const cleanPhone = (phone) => (phone[0] === "0" ? phone.slice(1) : phone);

  const sendVerification = async () => {
    if (isSendingRef.current || cooldownSeconds > 0) return;
    if (!canSendSms()) return;
    isSendingRef.current = true;

    try {
      setIsLoading(true);
      showMessage("Sending verification code...");
      const fullPhone = `${callingCode}${cleanPhone(phoneNumber)}`;
      const { data } = await axios.post(`${MyDomain}Otp/send`, {
        Phone: fullPhone,
      });

      if (data.success) {
        lastSmsSentTimeRef.current = Date.now();
        setOtpSent(true);
        showMessage("Verification code sent!", "success");
        setCooldownSeconds(SMS_COOLDOWN_SECONDS);
      } else {
        isSendingRef.current = false;
        showMessage("Failed to send OTP", "error");
      }
    } catch (err) {
      isSendingRef.current = false;
      showMessage(err.message || "Failed to send verification code", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const confirmCode = async () => {
    if (isVerifyingRef.current) return;
    isVerifyingRef.current = true;
    try {
      setIsLoading(true);
      showMessage("Verifying code...");
      const fullPhone = `${callingCode}${cleanPhone(phoneNumber)}`;
      const { data } = await axios.post(`${MyDomain}Otp/verify`, {
        Phone: fullPhone,
        Otp: verificationCode,
      });
      if (data.verified) {
        showMessage("Phone verification successful!", "success");
        setOpenSecondPageLogin(true);
      } else {
        showMessage("Invalid verification code", "error");
      }
    } catch (err) {
      showMessage(err.message || "Invalid verification code", "error");
    } finally {
      setIsLoading(false);
      setTimeout(() => {
        isVerifyingRef.current = false;
      }, 2000);
    }
  };

  const loginFunc = async () => {
    if (isLoggingInRef.current) return;
    isLoggingInRef.current = true;
    try {
      setIsLoading(true);
      const { data } = await axios.post(
        `${MyDomain}Barbers2/BarberLoginWithPhoneNumber`,
        {
          CountryNumber: callingCode,
          PhoneNumber: cleanPhone(phoneNumber),
          Name: name,
        },
      );
      localStorage.setItem("barber", JSON.stringify(data));
      window.location.reload();
      showMessage("Login successful! Redirecting...", "success");
    } catch {
      showMessage("Failed to complete login. Please try again.", "error");
    } finally {
      setIsLoading(false);
      setTimeout(() => {
        isLoggingInRef.current = false;
      }, 2000);
    }
  };

  const isSendDisabled = !phoneNumber || isLoading || cooldownSeconds > 0;
  const isVerifyDisabled = verificationCode.length !== 6 || isLoading;
  const isLoginDisabled = !name || isLoading;
  const isResendDisabled = isLoading || cooldownSeconds > 0;

  const msgColors = {
    info: { bg: "#1a1710", border: "#3a3020", text: "#c8a96e" },
    success: { bg: "#0d1a10", border: "#1a3a20", text: "#6ec88a" },
    warning: { bg: "#1a1500", border: "#3a3000", text: "#c8b96e" },
    error: { bg: "#1a0d0d", border: "#3a1a1a", text: "#c86e6e" },
  };

  return (
    <>
      <style>{fonts}</style>

      <div style={s.page}>
        {/* ── Decorative top ornament ── */}
        <div style={s.ornamentBar} />

        {/* ── Logo / brand header ── */}
        <div style={s.header}>
          <div style={s.logoRing}>
            <span style={s.logoScissor}>✂</span>
          </div>
          <p style={s.brandTagline}>THE BARBER STUDIO</p>
        </div>

        {/* ── Card ── */}
        <div style={s.card}>
          {/* Thin gold top accent */}
          <div style={s.cardAccentLine} />

          {/* ── Name Screen ── */}
          {openSecondPageLogin ? (
            <div style={s.form}>
              <div style={s.titleBlock}>
                <h1 style={s.title}>Welcome</h1>
                <p style={s.subtitle}>Enter your name to continue</p>
              </div>

              <div style={s.fieldGroup}>
                <label style={s.label}>Full Name</label>
                <input
                  style={s.input}
                  className="dark-input"
                  placeholder="Your full name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) =>
                    e.key === "Enter" && !isLoginDisabled && loginFunc()
                  }
                  autoCapitalize="words"
                  autoComplete="name"
                  dir="ltr"
                />
              </div>

              <button
                className="gold-btn"
                style={{
                  ...s.primaryBtn,
                  ...(isLoginDisabled ? s.btnDisabled : {}),
                }}
                onClick={loginFunc}
                disabled={isLoginDisabled}
              >
                {isLoading ? <Spinner /> : "Enter"}
              </button>
            </div>
          ) : !otpSent ? (
            /* ── Phone Screen ── */
            <div style={s.form}>
              <div style={s.titleBlock}>
                <h1 style={s.title}>Sign In</h1>
                <p style={s.subtitle}>Enter your phone number to get started</p>
              </div>

              <div style={s.fieldGroup}>
                <label style={s.label}>Phone Number</label>
                <div style={s.phoneRow}>
                  <CountryDropdown
                    value={callingCode}
                    onChange={(item) => setCallingCode(item.value)}
                  />
                  <input
                    style={{ ...s.input, flex: 1 }}
                    className="dark-input"
                    placeholder="5x-xxx-xxxx"
                    value={phoneNumber}
                    onChange={(e) => {
                      const v = e.target.value.replace(/[٠-٩]/g, (d) =>
                        "٠١٢٣٤٥٦٧٨٩".indexOf(d).toString(),
                      );
                      setPhoneNumber(v.replace(/[^0-9]/g, ""));
                    }}
                    onKeyDown={(e) =>
                      e.key === "Enter" && !isSendDisabled && sendVerification()
                    }
                    inputMode="tel"
                    type="tel"
                    autoComplete="tel"
                    dir="ltr"
                  />
                </div>
              </div>

              <button
                className="gold-btn"
                style={{
                  ...s.primaryBtn,
                  ...(isSendDisabled ? s.btnDisabled : {}),
                }}
                onClick={sendVerification}
                disabled={isSendDisabled}
              >
                {isLoading ? <Spinner /> : "Send Verification Code"}
              </button>

              <p style={s.hint}>We'll send a verification code via SMS</p>
            </div>
          ) : (
            /* ── OTP Screen ── */
            <div style={s.form}>
              <div style={s.titleBlock}>
                <h1 style={s.title}>Verify Code</h1>
                <p style={s.subtitle}>
                  6-digit code sent to{" "}
                  <span style={s.phoneHighlight}>
                    {callingCode} {phoneNumber}
                  </span>
                </p>
              </div>

              <div style={s.otpSection}>
                <OTPInput
                  code={verificationCode}
                  setCode={setVerificationCode}
                  length={6}
                />
              </div>

              <button
                className="gold-btn"
                style={{
                  ...s.primaryBtn,
                  ...(isVerifyDisabled ? s.btnDisabled : {}),
                }}
                onClick={confirmCode}
                disabled={isVerifyDisabled}
              >
                {isLoading ? <Spinner /> : "Verify Code"}
              </button>

              <button
                className="resend-btn"
                style={{
                  ...s.resendBtn,
                  ...(isResendDisabled ? { opacity: 0.4 } : {}),
                }}
                onClick={() => {
                  setVerificationCode("");
                  sendVerification();
                }}
                disabled={isResendDisabled}
              >
                {cooldownSeconds > 0 ? (
                  <span>
                    Resend in{" "}
                    <strong style={{ color: "#c8a96e" }}>
                      {cooldownSeconds}s
                    </strong>
                  </span>
                ) : (
                  <span>
                    Didn't receive code?{" "}
                    <span style={s.resendLink}>Resend</span>
                  </span>
                )}
              </button>
            </div>
          )}

          {/* ── Status message ── */}
          {message && (
            <div
              style={{
                ...s.msgBox,
                background: msgColors[messageType]?.bg,
                borderColor: msgColors[messageType]?.border,
              }}
            >
              <p style={{ ...s.msgText, color: msgColors[messageType]?.text }}>
                {message}
              </p>
            </div>
          )}
        </div>

        {/* ── Bottom ornament ── */}
        <div style={s.bottomOrnament}>✦ &nbsp; ✦ &nbsp; ✦</div>
      </div>
    </>
  );
}

// ── Spinner ──
function Spinner() {
  return (
    <div
      style={{
        width: 18,
        height: 18,
        border: "2px solid rgba(200,169,110,0.3)",
        borderTop: "2px solid #c8a96e",
        borderRadius: "50%",
        animation: "spin 0.7s linear infinite",
        display: "inline-block",
      }}
    >
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── Fonts & Global CSS ──
const fonts = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400&family=DM+Mono:wght@300;400;500&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #0f0d0a; }

  .dark-input:focus {
    border-color: #c8a96e !important;
    background: #1a1710 !important;
    outline: none;
    box-shadow: 0 0 0 3px rgba(200,169,110,0.08);
  }
  .dark-input::placeholder { color: #3a3028; }

  .gold-btn:hover:not(:disabled) {
    background: linear-gradient(135deg, #d4b87a, #b8923a) !important;
    box-shadow: 0 6px 24px rgba(200,169,110,0.35) !important;
    transform: translateY(-1px);
  }
  .gold-btn:active:not(:disabled) { transform: translateY(0); }

  .resend-btn:hover:not(:disabled) { opacity: 0.7; }

  .dropdown-item:hover { background: #1e1a14 !important; color: #c8a96e !important; }

  .otp-input:focus {
    border-color: #c8a96e !important;
    background: #1a1710 !important;
    outline: none;
    box-shadow: 0 0 0 3px rgba(200,169,110,0.08);
  }

  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track { background: #0f0d0a; }
  ::-webkit-scrollbar-thumb { background: #2a2520; border-radius: 2px; }
`;

// ── Styles ──
const s = {
  page: {
    minHeight: "100vh",
    background: "#0f0d0a",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    fontFamily: "'DM Mono', monospace",
    paddingBottom: 48,
  },

  // ── Top ornament bar ──
  ornamentBar: {
    width: "100%",
    height: "3px",
    background:
      "linear-gradient(90deg, transparent, #c8a96e 30%, #e8d4a0 50%, #c8a96e 70%, transparent)",
  },

  // ── Header ──
  header: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    paddingTop: 40,
    paddingBottom: 32,
    gap: 16,
  },
  logoRing: {
    width: 72,
    height: 72,
    borderRadius: "50%",
    border: "1.5px solid #2a2318",
    background: "radial-gradient(circle at 35% 35%, #1e1a12, #0f0d0a)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow:
      "0 0 0 6px #0f0d0a, 0 0 0 7px #1e1a12, inset 0 2px 8px rgba(0,0,0,0.6)",
  },
  logoScissor: {
    fontSize: 28,
    color: "#c8a96e",
    filter: "drop-shadow(0 0 6px rgba(200,169,110,0.4))",
  },
  brandTagline: {
    fontFamily: "'DM Mono', monospace",
    fontSize: "10px",
    letterSpacing: "0.35em",
    color: "#5a5048",
    textTransform: "uppercase",
  },

  // ── Card ──
  card: {
    width: "calc(100% - 32px)",
    maxWidth: 400,
    background: "#13110e",
    border: "1px solid #1e1a14",
    borderRadius: 4,
    padding: "0 28px 28px",
    boxShadow: "0 24px 64px rgba(0,0,0,0.6)",
    position: "relative",
    //overflow: "hidden",
  },
  cardAccentLine: {
    height: "2px",
    background:
      "linear-gradient(90deg, transparent, #c8a96e 20%, #e8d4a0 50%, #c8a96e 80%, transparent)",
    marginBottom: 28,
    marginLeft: -28,
    marginRight: -28,
  },

  // ── Form ──
  form: {
    display: "flex",
    flexDirection: "column",
  },
  titleBlock: {
    marginBottom: 28,
    textAlign: "center",
  },
  title: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 34,
    fontWeight: 600,
    color: "#e8ddd0",
    letterSpacing: "0.06em",
    marginBottom: 6,
    lineHeight: 1.1,
  },
  subtitle: {
    fontFamily: "'DM Mono', monospace",
    fontSize: "11px",
    color: "#5a5048",
    letterSpacing: "0.08em",
    lineHeight: 1.7,
  },
  phoneHighlight: {
    color: "#c8a96e",
  },

  // ── Fields ──
  fieldGroup: {
    marginBottom: 20,
  },
  label: {
    display: "block",
    fontFamily: "'DM Mono', monospace",
    fontSize: "10px",
    letterSpacing: "0.18em",
    color: "#7a6a5a",
    textTransform: "uppercase",
    marginBottom: 8,
  },
  input: {
    width: "100%",
    background: "#0f0d0a",
    border: "1px solid #2a2520",
    borderRadius: 2,
    padding: "13px 14px",
    fontSize: "14px",
    fontFamily: "'DM Mono', monospace",
    color: "#e8ddd0",
    outline: "none",
    minHeight: 48,
    direction: "ltr",
    transition: "border-color 0.2s, background 0.2s",
    letterSpacing: "0.05em",
  },
  phoneRow: {
    display: "flex",
    gap: 10,
    alignItems: "center",
  },

  // ── Dropdown ──
  dropdownWrapper: {
    position: "relative",
    minWidth: 120,
  },
  dropdownButton: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    background: "#0f0d0a",
    border: "1px solid #2a2520",
    borderRadius: 2,
    padding: "13px 12px",
    height: 48,
    width: "100%",
    fontSize: 13,
    color: "#e8ddd0",
    fontFamily: "'DM Mono', monospace",
    letterSpacing: "0.04em",
    outline: "none",
    cursor: "pointer",
  },
  dropdownCode: {
    flex: 1,
    textAlign: "left",
    fontSize: 12,
    color: "#c8a96e",
  },
  dropdownChevron: {
    fontSize: 8,
    color: "#5a5048",
  },
  dropdownMenu: {
    position: "absolute",
    top: "calc(100% + 4px)",
    left: 0,
    zIndex: 999,
    background: "#13110e",
    border: "1px solid #2a2520",
    borderRadius: 2,
    boxShadow: "0 16px 48px rgba(0,0,0,0.7)",
    minWidth: 240,
    overflow: "hidden",
  },
  dropdownSearch: {
    width: "100%",
    border: "none",
    borderBottom: "1px solid #2a2520",
    padding: "10px 14px",
    fontSize: 12,
    fontFamily: "'DM Mono', monospace",
    outline: "none",
    background: "#0f0d0a",
    color: "#e8ddd0",
    letterSpacing: "0.04em",
  },
  dropdownList: {
    maxHeight: 200,
    overflowY: "auto",
  },
  dropdownItem: {
    padding: "10px 14px",
    fontSize: 13,
    color: "#7a6a5a",
    cursor: "pointer",
    fontFamily: "'DM Mono', monospace",
    letterSpacing: "0.02em",
    transition: "background 0.12s, color 0.12s",
  },
  dropdownItemActive: {
    background: "#1a1710",
    color: "#c8a96e",
  },

  // ── OTP ──
  otpSection: {
    marginBottom: 24,
  },
  otpContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(6, 1fr)",
    gap: 8,
    width: "100%",
  },
  otpInput: {
    width: "100%",
    minWidth: 0,
    height: 52,
    padding: 0,
    border: "1px solid #2a2520",
    borderRadius: 2,
    textAlign: "center",
    fontSize: 20,
    fontWeight: 500,
    fontFamily: "'DM Mono', monospace",
    background: "#0f0d0a",
    color: "#e8ddd0",
    outline: "none",
    direction: "ltr",
    transition: "border-color 0.2s, background 0.2s",
  },
  otpInputFilled: {
    borderColor: "#c8a96e",
    background: "#1a1710",
    color: "#c8a96e",
  },

  // ── Buttons ──
  primaryBtn: {
    width: "100%",
    background: "linear-gradient(135deg, #c8a96e, #a08040)",
    border: "none",
    borderRadius: 2,
    padding: "14px 0",
    fontSize: "12px",
    fontWeight: 500,
    fontFamily: "'DM Mono', monospace",
    letterSpacing: "0.2em",
    textTransform: "uppercase",
    color: "#0f0d0a",
    boxShadow: "0 4px 20px rgba(200,169,110,0.2)",
    minHeight: 48,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  btnDisabled: {
    background: "#1e1a14",
    color: "#3a3028",
    boxShadow: "none",
    cursor: "not-allowed",
  },
  resendBtn: {
    background: "none",
    border: "none",
    padding: "14px 0",
    marginTop: 8,
    width: "100%",
    textAlign: "center",
    fontFamily: "'DM Mono', monospace",
    fontSize: "11px",
    color: "#5a5048",
    letterSpacing: "0.06em",
    cursor: "pointer",
    transition: "opacity 0.2s",
  },
  resendLink: {
    color: "#c8a96e",
    borderBottom: "1px solid rgba(200,169,110,0.3)",
  },

  hint: {
    marginTop: 16,
    fontFamily: "'DM Mono', monospace",
    fontSize: "10px",
    color: "#3a3028",
    letterSpacing: "0.1em",
    textAlign: "center",
    lineHeight: 1.6,
  },

  // ── Message ──
  msgBox: {
    marginTop: 16,
    padding: "10px 14px",
    borderRadius: 2,
    border: "1px solid",
  },
  msgText: {
    fontFamily: "'DM Mono', monospace",
    fontSize: "11px",
    textAlign: "center",
    letterSpacing: "0.06em",
    lineHeight: 1.6,
  },

  // ── Bottom ornament ──
  bottomOrnament: {
    marginTop: 32,
    fontFamily: "'DM Mono', monospace",
    fontSize: "10px",
    color: "#2a2520",
    letterSpacing: "0.3em",
  },
};
